import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { pluralize } from "@entrilia/shared/utilities/stringHelper";
import biApi from "../../../../../api/biApi";
import type { WorkbookTemplate } from "../../../../../api/types/workbookRuns.types";

const deleteTemplates = withErrorHandling(biApi.deleteWorkbookTemplates);

interface Props {
  templates: WorkbookTemplate[];
  onClose: () => void;
  onDeleted: (templateIds: string[]) => void;
}

const DeleteTemplatesDialog = ({ templates, onClose, onDeleted }: Props) => {
  const { sendNotificationError, sendNotification } = useNotificationContext();
  const [deleting, setDeleting] = useState(false);

  const singleTemplate = templates.length === 1 ? templates[0] : undefined;

  const handleDeleteTemplates = async () => {
    setDeleting(true);
    const templateIds = templates.map((template) => template.id);
    const [, error] = await deleteTemplates({ templateIds });
    setDeleting(false);
    if (error) {
      logError(error, "[DeleteTemplatesDialog] deleteTemplates");
      sendNotificationError(error.message);
      return;
    }
    sendNotification(`${pluralize("Template", templates.length)} deleted successfully`);
    onDeleted(templateIds);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>{`Delete ${pluralize("Template", templates.length)}`}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center", minWidth: 0 }}>
            <Typography variant="h6" sx={{ textAlign: "center", wordBreak: "break-word" }}>
              {singleTemplate ? (
                <>
                  Delete <strong>{singleTemplate.name}</strong> template?
                </>
              ) : (
                `Delete ${templates.length} templates?`
              )}
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {singleTemplate
                ? "You're about to delete the template. This action is permanent and can't be undone."
                : "You're about to delete the templates. This action is permanent and can't be undone."}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={deleting} onClick={handleDeleteTemplates}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteTemplatesDialog;
